import * as Popover from '@radix-ui/react-popover';
import { Mail, Phone, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { BookingDetailActions, DetailsChannelMark, PlanBanner } from './BookingDetailActions';
import type { BookingDetailsModel } from './bookingDetailsModel';
import type { BookingSpan } from './calendarHelpers';

interface Props {
  span: BookingSpan;
  model: BookingDetailsModel;
  propertyId: string;
  canOpenOnboarding: boolean;
  canReassign?: boolean;
  onReassignRequested?: () => void;
}

const eyebrow = 'text-ink-soft font-mono text-[10px] uppercase tracking-[0.14em]';

/**
 * Details einer Buchung im Popover am Desktop (Board „Belegung“): Kanal, Name nach Namensregel,
 * Fakten mit den Uhrzeiten des Objekts, Kontakte nur mit Gastdaten-Recht, Plan-Banner und die
 * nächsten Schritte. Schließen per Knopf und Esc.
 */
export function BookingDetails({ span, model, propertyId, ...rights }: Props) {
  return (
    <div className="text-ink flex flex-col gap-3 p-[18px]">
      <div className="flex items-start gap-2.5">
        <DetailsChannelMark span={span} size={30} />
        <span className="flex min-w-0 grow flex-col">
          <span className="truncate font-serif text-[19px] font-bold tracking-[-0.01em]">
            {model.title}
          </span>
          <span className="text-ink-soft truncate font-mono text-[11px]">{model.sub}</span>
        </span>
        <Popover.Close asChild>
          <button
            type="button"
            aria-label="Schließen"
            className="border-hairline rounded-pill grid size-9 flex-none place-items-center border"
          >
            <X size={14} aria-hidden />
          </button>
        </Popover.Close>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
        {model.facts.map((fact) => (
          <span key={fact.label} className="flex min-w-0 flex-col">
            <span className={eyebrow}>{fact.label}</span>
            <b className="truncate">{fact.value}</b>
          </span>
        ))}
        {model.contacts && (
          <>
            <Contact label="Telefon" icon={<Phone size={12} aria-hidden />}>
              {model.contacts.phone && (
                <a href={`tel:${model.contacts.phone}`}>{model.contacts.phone}</a>
              )}
            </Contact>
            {model.contacts.email !== undefined && (
              <Contact label="E-Mail" icon={<Mail size={12} aria-hidden />}>
                {model.contacts.email && (
                  <a href={`mailto:${model.contacts.email}`}>{model.contacts.email}</a>
                )}
              </Contact>
            )}
          </>
        )}
      </div>
      {model.plan && <PlanBanner plan={model.plan} className="px-2.5 py-2 text-xs" />}
      <BookingDetailActions span={span} propertyId={propertyId} variant="popover" {...rights} />
    </div>
  );
}

function Contact({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className={`${eyebrow} flex items-center gap-1`}>
        {icon}
        {label}
      </span>
      {children ? (
        <b className="text-forest truncate underline-offset-2 hover:underline">{children}</b>
      ) : (
        <span className="text-ink-faint">nicht hinterlegt</span>
      )}
    </span>
  );
}
